// Cross-identity session teardown.
//
// A single tab can hold both an admin session and a candidate session (the
// portal and the admin shell share the origin). Signing out of one used to
// leave the other fully usable, and neither cleared the react-query cache, so
// the next person on that tab was served the previous session's candidate PII
// out of cache before any refetch landed.
import { clearAccessToken } from './api';
import { clearCandidateToken, candidateLogout } from './candidateApi';
import queryClient from './queryClient';

const CHANNEL_NAME = 'auth';
const LOGOUT_MESSAGE = 'logout';

let channel;

function getChannel() {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (channel === undefined) {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
    } catch {
      channel = null;
    }
  }
  return channel;
}

/**
 * Drop every credential this tab holds (admin token, candidate token) and the
 * entire query cache. Safe to call more than once.
 */
export function resetAllIdentities() {
  clearAccessToken();
  clearCandidateToken();
  queryClient.clear();
}

/** Tell the other tabs on this origin that the user signed out. */
export function publishLogout() {
  try {
    getChannel()?.postMessage({ type: LOGOUT_MESSAGE, at: Date.now() });
  } catch {
    // BroadcastChannel is best effort; a closed channel must not break logout
  }
}

/**
 * Listen for a logout in another tab. The access token is memory-only, so a
 * tab that is not told will keep working with its cached PII for the whole
 * token lifetime.
 * @param {() => void} handler
 * @returns {() => void} unsubscribe
 */
export function subscribeToLogout(handler) {
  const ch = getChannel();
  if (!ch) return () => {};
  const listener = (event) => {
    if (event?.data?.type === LOGOUT_MESSAGE) handler();
  };
  ch.addEventListener('message', listener);
  return () => ch.removeEventListener('message', listener);
}

/** Portal sign-out: revoke server-side, then clear both identities everywhere. */
export async function logoutCandidateEverywhere() {
  await candidateLogout();
  resetAllIdentities();
  publishLogout();
}
